import { useRef } from 'react';
import { useAuth } from 'react-oidc-context';

export default function useIdentity() {
    const auth = useAuth();
    const _getAccessTokenPromise = useRef<Promise<string> | null>(null);

    const getAccessToken = async (): Promise<string> => {
        // Return the cached promise if one is in progress
        if (_getAccessTokenPromise.current) {
            return _getAccessTokenPromise.current;
        }

        // Create and cache a new promise
        _getAccessTokenPromise.current = (async () => {
            try {
                // Check if user is valid and token is not expired
                if (auth.user && !auth.user.expired) {
                    console.log("%c[useIdentity] Returning existing access token:", "color: green;", auth.user.access_token);
                    return auth.user.access_token;
                }

                // Attempt silent sign-in if user is null or token is expired
                // signinSilent returns the new User object directly
                const newUser = await auth.signinSilent();

                // Check if signin was successful
                if (!newUser) {
                    throw new Error('Failed to get user after silent sign-in');
                }

                return newUser.access_token;
            } catch (error) {
                console.error('Error getting access token:', error);
                throw error;
            } finally {
                // Clear the cached promise when operation completes (success or failure)
                _getAccessTokenPromise.current = null;
            }
        })();

        return _getAccessTokenPromise.current;
    };

    return {
        signin: auth.signinSilent,
        loading: auth.isLoading,
        error: auth.error,
        isAuthenticated: auth.isAuthenticated,
        user: auth.user,
        getAccessToken,
    };
}
